/**
 * Push Notification Manager Component - Trade Loop v2.0
 *
 * The push section of the Account page.
 *
 * ⚠️ CARD 30 — PUSH IS NATIVE-ONLY NOW. Web push was Web Push (VAPID), delivered by the
 * service worker at /sw-push.js. Retiring the PWA removed that worker, and there is no other
 * delivery path in a browser, so every web subscribe/unsubscribe path here is gone rather
 * than left to fail. On web this renders a short pointer at the mobile apps. Everything below
 * about native is unchanged.
 *
 * ⚠️ CARD 17 — WHY THIS COMPONENT WAS REWRITTEN. It used to derive `isSubscribed` from the
 * WEB PUSH service-worker subscription:
 *
 *     const registration = await navigator.serviceWorker.ready;
 *     const subscription = await registration.pushManager.getSubscription();
 *     setIsSubscribed(!!subscription);
 *
 * A native build has no such subscription — its FCM token lives on a separate path — so on
 * Android and iOS `isSubscribed` was permanently false and the *Send Test Notification*
 * button never rendered. Worse, `isSupported` also failed, so the component fell into its
 * unsupported-browser branch and told the user "Your browser may not support push
 * notifications. Please use a modern browser like Chrome, Firefox, Edge…" — inside a native
 * app, where there is no browser to change, and on Android where push demonstrably works.
 * A user or support agent reading that stops looking. Confirmed on both platforms during
 * Card 16 device testing (2026-08-01).
 *
 * Two rules follow, and both are load-bearing:
 *   1. On native, subscription state is keyed off the NATIVE TOKEN, never the SW subscription.
 *   2. No browser-centric copy is ever rendered when `isNative`.
 *
 * It also gives a denied permission a route back. `PushNotificationPrompt` only shows its
 * modal when the status is 'prompt', so a user who denied in an earlier session got nothing,
 * forever. Re-prompting cannot fix that — Android 13+ refuses to show the OS dialog again
 * after two denials — so the only honest remedy is to point at system settings, which is
 * what the denied branch below does, per platform.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { isNative, platform } from '@/utils/platform';
import {
  checkPushPermissionStatus,
  registerForPushNotifications,
  requestPushPermission
} from '@/services/capacitor/pushNotifications';
import {
  getLocalTokenState,
  probeNativePushPrerequisites
} from '@/services/capacitor/pushDiagnostics';
import type { NativePushPrerequisites } from '@/types/pushDiagnostics.types';

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8080";

/** Where to send a user whose OS permission is denied. There is no in-app route back. */
function systemSettingsInstruction(): string {
  return platform === 'ios'
    ? 'Open Settings → Notifications → Trade Loop and turn on Allow Notifications.'
    : 'Open Settings → Apps → Trade Loop → Notifications and turn notifications on.';
}

export function PushNotificationManager() {
  const { showSuccess, showError, showInfo } = useToast();
  const { user } = useAuth();

  // State management
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const [permission, setPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  /** Native prerequisite probe. Null on web, where it does not apply. */
  const [prerequisites, setPrerequisites] = useState<NativePushPrerequisites | null>(null);

  /**
   * Determine what this device can do, and what its current state is.
   *
   * Native only — "supported" means the plugin and config are actually usable, not whether
   * some browser API exists. Asking the browser's PushManager about a native build is what
   * produced the Card 17 bug described above.
   */
  useEffect(() => {
    let cancelled = false;

    const initialise = async () => {
      if (!isNative) return;

      const probe = await probeNativePushPrerequisites();
      const [status, token] = await Promise.all([
        checkPushPermissionStatus(),
        getLocalTokenState()
      ]);
      if (cancelled) return;

      setPrerequisites(probe);
      setIsSupported(probe.supported);
      setPermission(status);
      setIsSubscribed(token.present);
    };

    void initialise();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Request notification permission from user
   */
  const requestPermission = async (): Promise<boolean> => {
    try {
      const result = await requestPushPermission();
      setPermission(result);

      if (result === 'granted') {
        showSuccess('Permission Granted', 'You can now receive push notifications');
        return true;
      } else if (result === 'denied') {
        // Do NOT tell a native user to change a browser setting. The remedy below is the
        // real one, and on Android 13+ it is the only one left.
        showError('Permission Denied', systemSettingsInstruction());
        return false;
      } else {
        showInfo('Permission Dismissed', 'You can enable notifications later in settings');
        return false;
      }
    } catch (error) {
      console.error('[Push] Error requesting permission:', error);
      showError('Permission Error', 'Failed to request notification permission');
      return false;
    }
  };

  /**
   * Subscribe on native: permission, then register. The token reaches the server through
   * `registerForPushNotifications`, which owns registration and is idempotent.
   */
  const handleNativeSubscribe = async () => {
    if (permission !== 'granted') {
      const granted = await requestPermission();
      if (!granted) return;
    }

    await registerForPushNotifications();

    // Registration is asynchronous — the token arrives on the `registration` listener — so
    // re-read rather than assuming success. If it did not arrive, say nothing changed.
    const token = await getLocalTokenState();
    setIsSubscribed(token.present);

    if (token.present) {
      showSuccess('Subscribed!', 'This device will now receive push notifications');
    } else {
      const probe = await probeNativePushPrerequisites();
      setPrerequisites(probe);
      showError(
        'Registration incomplete',
        probe.reason
          ? 'This build cannot register for push. Open Push Diagnostics for details.'
          : 'No token has arrived yet. Reopen the app, then check Push Diagnostics.'
      );
    }
  };

  const handleNativeUnsubscribe = async () => {
    const token = await getLocalTokenState();

    try {
      await fetch(`${API_BASE_URL}/api/push/unsubscribe`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token: localStorage.getItem('push_token') })
      });
    } finally {
      // The OS permission stays granted; this only stops the server sending here. Clearing
      // the local copy is what makes the toggle honest on the next read.
      localStorage.removeItem('push_token');
      setIsSubscribed(false);
    }

    if (token.present) {
      showSuccess('Unsubscribed', 'This device will no longer receive push notifications');
    }
  };

  /**
   * Subscribe to push notifications
   */
  const handleSubscribe = async () => {
    setIsLoading(true);

    try {
      await handleNativeSubscribe();
    } catch (error: any) {
      console.error('[Push] Subscription error:', error);
      showError('Subscription Failed', error.message || 'Failed to subscribe to push notifications');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Unsubscribe from push notifications
   */
  const handleUnsubscribe = async () => {
    setIsLoading(true);

    try {
      await handleNativeUnsubscribe();
    } catch (error: any) {
      console.error('[Push] Unsubscribe error:', error);
      showError('Unsubscribe Failed', error.message || 'Failed to unsubscribe from push notifications');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Send test notification
   */
  const handleTestNotification = async () => {
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/push/test`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send the sample notification');
      }

      const result = await response.json();
      console.log('[Push] Test notification result:', result);

      showSuccess('Sample sent', 'Check your notifications');
    } catch (error: any) {
      console.error('[Push] Test notification error:', error);
      showError("Couldn't send", error.message || 'Failed to send the sample notification');
    } finally {
      setIsLoading(false);
    }
  };

  // WEB. Push notifications are a native-app feature now: web delivery went through the
  // service worker, which Card 30 retired along with the rest of the PWA. Say that plainly
  // rather than showing a toggle that cannot work, or the old "use a modern browser" copy
  // that blamed the user's browser for a capability we removed on purpose.
  if (!isNative) {
    return (
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
        <div className="flex items-start">
          <svg className="h-6 w-6 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-gray-900">
              Push notifications are in the mobile app
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              Install Trade Loop for iPhone, iPad or Android to get alerts about new messages
              and referral updates. Everything else works the same here in your browser.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // A native build whose plugin or Firebase config is missing. This must say what is
  // actually wrong — it is the exact case that used to render "use a modern browser".
  if (!isSupported) {
    return (
      <div className="bg-white shadow-sm border border-red-200 rounded-lg p-6">
        <div className="flex items-start">
          <svg className="h-6 w-6 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div className="ml-3 space-y-2">
            <h3 className="text-sm font-medium text-gray-900">
              This app build cannot receive notifications
            </h3>
            <p className="text-sm text-gray-600">
              {prerequisites?.reason === 'plugin_missing'
                ? 'The push notification plugin is not included in this build.'
                : prerequisites?.reason === 'firebase_config_missing'
                  ? 'The Firebase configuration file is missing from this build.'
                  : 'Push registration failed on this device.'}
              {' '}This is a problem with the app build, not with your device or settings.
            </p>
            {prerequisites?.detail ? (
              <pre className="text-xs text-gray-500 whitespace-pre-wrap bg-gray-50 rounded p-2">
                {prerequisites.detail}
              </pre>
            ) : null}
            {user?.isAdmin ? (
              <Link to="/admin/push-diagnostics" className="text-sm text-blue-600 underline">
                Open Push Diagnostics
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  const handleToggle = async (enabled: boolean) => {
    if (enabled) {
      await handleSubscribe();
    } else {
      await handleUnsubscribe();
    }
  };

  return (
    <div className="space-y-6">
      {/* Push Notification Toggle */}
      <div className="flex items-start justify-between pt-4 border-t border-gray-100">
        <div className="flex-1 pr-4">
          <h4 className="font-medium text-gray-900 mb-1">Enable Push Notifications</h4>
          <p className="text-sm text-gray-600">
            Receive alerts on this device about important updates
          </p>

          {/* Status Badge */}
          <div className="mt-2 flex items-center gap-2">
            {permission === 'granted' && isSubscribed ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <svg className="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 8 8">
                  <circle cx="4" cy="4" r="3" />
                </svg>
                Active
              </span>
            ) : permission === 'denied' ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Blocked
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                Not Enabled
              </span>
            )}
          </div>
        </div>
        <Switch
          checked={isSubscribed}
          onCheckedChange={handleToggle}
          disabled={isLoading || permission === 'denied'}
        />
      </div>

      {/* Test Notification Button */}
      {isSubscribed && (
        <div className="pt-4 border-t border-gray-100">
          <Button
            onClick={handleTestNotification}
            disabled={isLoading}
            variant="outline"
            size="sm"
            className="border-purple-300 hover:bg-purple-50 text-purple-700"
          >
            <svg
              className="-ml-1 mr-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
            {isLoading ? 'Sending...' : 'Send a sample notification'}
          </Button>
        </div>
      )}

      {/*
        Permission Denied Help — the "route back" for a user who denied in an EARLIER
        session. `PushNotificationPrompt` only opens on status 'prompt', so without this
        such a user has no path at all, and re-prompting is not one: Android 13+ stops
        showing the OS dialog after two denials.
      */}
      {permission === 'denied' && (
        <div className="mt-4 rounded-md bg-yellow-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Notifications Blocked
              </h3>
              <div className="mt-2 text-sm text-yellow-700 space-y-1">
                <p>{systemSettingsInstruction()}</p>
                <p className="text-xs">
                  The app cannot ask again — {platform === 'android' ? 'Android' : 'iOS'} only
                  offers that prompt once, so this has to be changed in system settings.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admins get a route to the full diagnostics from the device itself (Card 17). */}
      {user?.isAdmin ? (
        <div className="pt-4 border-t border-gray-100">
          <Link to="/admin/push-diagnostics" className="text-sm text-blue-600 hover:underline">
            Open Push Diagnostics →
          </Link>
        </div>
      ) : null}
    </div>
  );
}
